import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { DSSetup, RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type { RegressorMetricData } from 'webapp/models-custom/regressor-metric';
import type ForecastRecord from 'webapp/models/forecast';

export default class ForecastSerializer extends ApplicationSerializer {
	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Forecast.Raw | RecordData.Forecast.Serialized,
		prop: string | undefined,
	) {
		const { metricSetup, regressors, ...data } = json;
		const attributes: Partial<RecordAttributes<ForecastRecord>> & { id?: string | number } = {
			...data,
		};

		if (metricSetup) {
			const normalizedMetricSetup = this.normalizeMetricSetupData(
				metricSetup as DSSetup.MetricSetupData,
			)!;
			attributes.metricSetupKey = normalizedMetricSetup.key;
			attributes.metricSetupSpaceAccessId = normalizedMetricSetup.spaceAccessId;
			attributes.metricSetupDimensions = normalizedMetricSetup.dimensions || [];
			attributes.metricSetupFilters = normalizedMetricSetup.filters || null;
		}

		// regressors go through the regressor-metric serializer, so they are flattened the same way
		if (regressors) {
			const regressorSerializer = this.store.serializerFor('regressor-metric');
			const regressorModelClass = this.store.modelFor('regressor-metric');

			attributes.regressors = regressors.map((regressor) => {
				const normalizedRegressor = regressorSerializer.normalize(
					regressorModelClass,
					regressor,
					'regressor-metric',
				);
				const { id, attributes } = normalizedRegressor.data,
					regressorAttributes = attributes as RegressorMetricData;
				// RegressorMetric falls back to a temporary id, which is the same for regressors created together
				return id ? { ...regressorAttributes, id } : regressorAttributes;
			});
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Forecast.Serialized {
		const attributes = super.serialize(snapshot, options) as RecordAttributes<ForecastRecord>;

		return {
			...omit(
				attributes,
				'metricSetupKey',
				'metricSetupSpaceAccessId',
				'metricSetupDimensions',
				'metricSetupFilters',
				'regressors',
			),
			metricSetup: this.serializeMetricSetupData({
				key: attributes.metricSetupKey,
				spaceAccessId: attributes.metricSetupSpaceAccessId,
				dimensions: attributes.metricSetupDimensions,
				filters: attributes.metricSetupFilters,
			}),
			regressors: attributes.regressors.map((regressor) => ({
				metricSetup: this.serializeMetricSetupData({
					key: regressor.metricSetupKey,
					spaceAccessId: regressor.metricSetupSpaceAccessId,
					dimensions: regressor.metricSetupDimensions,
					filters: regressor.metricSetupFilters,
				})!,
				methodology: regressor.methodology,
				correlation: regressor.correlation,
				enabled: regressor.enabled ?? true, // RegressorMetric's default
			})),
		};
	}
}
